/**
 * Viewport visibility helpers.
 *
 * Why this exists: components that render inside the always-mounted
 * MultiTableGrid panel keep processing live game data even while that panel is
 * slid off-screen (transform: translate3d(-100%, 0, 0)) behind the lobby. Any
 * such component that `createPortal`s to `document.body` and positions itself
 * by a table/seat element's getBoundingClientRect() will, for an OFF-SCREEN
 * table, place a position:fixed element outside the viewport. On iOS WKWebView
 * that grows the scrollable content area and scrolls the page, shoving the
 * (also position:fixed) lobby shell up — the "lobby jumps a second after
 * returning from a table" bug.
 *
 * RULE: any component that portals to document.body positioned by a table/seat
 * element's rect MUST guard with isElementOnScreen()/isRectOnScreen() so it
 * doesn't paint for a table nobody is looking at. The background table keeps
 * running — only the off-screen visual is skipped. See WinChips.tsx for the
 * canonical usage.
 */

type RectLike = Pick<DOMRect, 'left' | 'right' | 'top' | 'bottom' | 'width' | 'height'>;

const viewportWidth = (): number => (typeof window !== 'undefined' ? window.innerWidth : 0);
const viewportHeight = (): number => (typeof window !== 'undefined' ? window.innerHeight : 0);

/**
 * True when the rect overlaps the visible viewport at all.
 *
 * Returns false when the rect is fully past any edge (e.g. left:-812 → right:0
 * for a table in the off-screen grid panel) or has zero area (not laid out).
 * A partially-visible rect counts as on-screen.
 */
export const isRectOnScreen = (
  rect: RectLike,
  vw: number = viewportWidth(),
  vh: number = viewportHeight()
): boolean => {
  if (rect.width === 0 && rect.height === 0) return false;
  if (rect.right <= 0 || rect.bottom <= 0 || rect.left >= vw || rect.top >= vh) return false;
  return true;
};

/** Convenience wrapper: measures the element and defers to isRectOnScreen. */
export const isElementOnScreen = (el: Element | null): boolean => {
  if (!el) return false;
  return isRectOnScreen(el.getBoundingClientRect());
};
